import { GetStatementsAction, ResetStatementsAction } from "@merchant_app/storage/statements/statements.actions";
import { getStatements, getStatementsLoading } from "@merchant_app/storage/statements/statements.selectors";
import { IStore } from "@merchant_app/storage/store";
import { OnDestroy, OnInit } from "@angular/core";
import { Component, Inject } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { Month } from "@merchant_api/models/month";
import { StatementReportItem } from "@merchant_api/models/statement-report-item";
import { Subject, Subscription } from "rxjs";
import { ActionButtonKind, LocalTimePoint } from "shared-lib";
import { ModalActionType } from "../modal-action";
import { DownloadReportModalAction, DownloadReportParams } from "./download-report-modal-action";
import { ReportType } from "./report-type";
import { MerchantProcessorDto } from "@merchant_api/models/merchant-processor-dto";
import { getMids, getMidsLoading } from "@merchant_app/storage/mids/mids.selectors";
import { GetMidsAction, ResetMidsAction } from "@merchant_app/storage/mids/mids.actions";

@Component({
    selector: "app-download-report-modal",
    templateUrl: "./download-report-modal.component.html",
    styleUrls: ["./download-report-modal.component.less"],
    standalone: false
})
export class DownloadReportModalComponent implements OnInit, OnDestroy {

    get formControls() {
        return this.reportForm && this.reportForm.controls;
    }

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store<IStore>,
        public dialogRef: MatDialogRef<DownloadReportModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            reportType: ReportType;
            errorMessageSubject: Subject<string>;
            modalActionSubject: Subject<DownloadReportModalAction>;
        }
    ) {
        this.calculateMaxMonthAndYear();
    }

    ReportType = ReportType;
    LocalTimePoint = LocalTimePoint;

    mids$ = this.store.select(getMids);
    midsLoading$ = this.store.select(getMidsLoading);

    mids: MerchantProcessorDto[] = [];
    midsLoading = false;

    statements$ = this.store.select(getStatements);
    statementsLoading$ = this.store.select(getStatementsLoading);

    statements: Array<StatementReportItem> = [];
    statementsLoading = false;

    private subscriptions = new Subscription();

    private readonly localTimePoint = new LocalTimePoint();
    private maxMonth: number = null;
    private maxYear: number = null;

    get months(): Array<string> {
        let retVal = ["January","February","March","April","May","June","July","August","September","October","November","December"];
        if (this.hasYear) {
            const year = this.reportForm.get("yearField").value;
            if (year === this.maxYear) {
                retVal = retVal.slice(0, this.maxMonth);
            }
        }
        return retVal;
    }

    get years(): Array<number> {
        let retVal = [];
        const minYear = 2021;
        let yearToAdd = this.maxYear;

        do {
            retVal.push(yearToAdd);
        } while (yearToAdd-- > minYear);

        if (this.hasMonth) {
            const month = this.reportForm.get("monthField").value;
            if (month > this.maxMonth) {
                retVal = retVal.slice(1);
            }
        }
        return retVal;
    }

    ActionButtonKind = ActionButtonKind;

    reportForm = this.fb.group({
        idField: [{value:null, disabled: true}, [Validators.required]],
        dateSingleField: [{value:null, disabled: true}, [Validators.required]],
        dateRangeField: [{value:null, disabled: true}, [Validators.required]],
        midField: [{value:null, disabled: true}, /*this.reportType === ReportType.RECONCILIATION ? [Validators.required] :*/ []],
        statementField: [{value:null, disabled: true}, [Validators.required]],
        monthField: [{value:null, disabled: true}, [Validators.required]],
        yearField: [{value:null, disabled: true}, [Validators.required]]
    });

    get reportType(): string {
        return (this.data.reportType === ReportType.STATEMENT ? this.data.reportType : this.data.reportType + " Report");
    }

    getFormControl(name: string): UntypedFormControl {
        return this.formControls[name] as UntypedFormControl;
    }

    get optionsForMonths() {
        return this.months.map((month, index) => ({label: month, value: index + 1}));
    }

    ngOnInit() {
        this.disableFormFieldsForOtherReportTypes();

        switch (this.data.reportType) {
            case ReportType.TRANSACTIONS:
            case ReportType.FUNDING: {
                this.subscriptions.add(this.mids$.subscribe((mids: MerchantProcessorDto[]) => {
                    this.mids = mids;
                }));
                this.subscriptions.add(this.midsLoading$.subscribe((midsLoading: boolean) => {
                    this.midsLoading = midsLoading;
                }));
                this.resetMidField();
                this.store.dispatch(new GetMidsAction());
                break;
            }
            case ReportType.STATEMENT: {
                this.subscriptions.add(this.statements$.subscribe((statements: Array<StatementReportItem>) => {
                    this.statements = statements;
                }));
                this.subscriptions.add(this.statementsLoading$.subscribe((statementsLoading: boolean) => {
                    this.statementsLoading = statementsLoading;
                }));
                this.resetStatementField();
                break;
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onMonthChanged() {
        switch (this.data.reportType) {
            case ReportType.STATEMENT: {
                this.getStatementsIfRequired();
                break;
            }
        }
    }

    onYearChanged() {
        switch (this.data.reportType) {
            case ReportType.STATEMENT: {
                this.getStatementsIfRequired();
                break;
            }
        }
    }

    get hasMonth(): boolean {
        const monthField = this.reportForm.get("monthField");
        return (monthField && (monthField.value !== null));
    }

    get hasYear(): boolean {
        const yearField = this.reportForm.get("yearField");
        return (yearField && (yearField.value !== null));
    }

    get noStatements(): boolean {
        return (!this.statementsLoading) && !(this.statements?.length);
    }

    get noMids(): boolean {
        return (!this.midsLoading) && !(this.mids?.length);
    }

    onMouseDown() {
        this.data.errorMessageSubject.next("");
    }

    onCancel(event?: any) {
        if (event) {
            event.preventDefault();
        }
        this.data.modalActionSubject.next(new DownloadReportModalAction(ModalActionType.CANCEL));
    }

    onSubmit() {

        const params: DownloadReportParams = {};

        switch (this.data.reportType) {
            case ReportType.TRANSACTIONS:
            case ReportType.FUNDING: {
                params.merchantProcessorIds = this.getSubmitValueForField("midField");
                const dateRange = this.getSubmitValueForField("dateRangeField");
                if (dateRange) {
                    params.startDate = dateRange.from;
                    params.endDate = dateRange.to;
                }
                break;
            }
            case ReportType.STATEMENT: {
                params.statementId = this.getSubmitValueForField("statementField");
                break;
            }
        }

        this.data.modalActionSubject.next(new DownloadReportModalAction(ModalActionType.CONFIRM, params));
    }

    private getSubmitValueForField(fieldName: string): any {
        const fieldValue = this.reportForm.get(fieldName).value;
        return (fieldValue ? fieldValue : undefined);
    }

    private disableFormFieldsForOtherReportTypes() {
        switch (this.data.reportType) {
            case ReportType.TRANSACTIONS: {
                this.reportForm.get("dateRangeField").enable();
                break;
            }
            case ReportType.FUNDING: {
                this.reportForm.get("dateRangeField").enable();
                break;
            }
            case ReportType.STATEMENT: {
                this.reportForm.get("monthField").enable();
                this.reportForm.get("yearField").enable();
                this.reportForm.get("statementField").enable();
                break;
            }
        }
    }

    private resetStatementField(): void {
        this.reportForm.get("statementField").setValue(null);   // Workaround Angular issue
        this.store.dispatch(new ResetStatementsAction());
    }

    private resetMidField(): void {
        this.reportForm.get("midField").setValue(null);         // Workaround Angular issue
        this.store.dispatch(new ResetMidsAction());
    }

    private calculateMaxMonthAndYear(): void {
        const date = this.localTimePoint.asNgbDate;
        let month = date.month;
        let year = date.year;
        // manual calculation of previous month => accurate
        month -= 1;
        if (month <= 0) {
            month = 12;
            year -= 1;
        }
        this.maxMonth = month;
        this.maxYear = year;
    }

    private getStatementsIfRequired(): void {
        if (this.hasMonth && this.hasYear) {
            this.resetStatementField();
            const month = this.getMonthForMonthVal(this.reportForm.get("monthField").value);
            const year = this.reportForm.get("yearField").value;
            const merchantId = this.getSubmitValueForField("midField");
            this.store.dispatch(new GetStatementsAction(month as Month, year, merchantId));
        }
    }

    private getMonthForMonthVal(monthVal: number): string {
        const months = Array.from({length: 12}, (_item, i) => new Date(0, i).toLocaleString("en-US", {month: "long"}));
        return months[monthVal - 1];
    }

}
